import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'
import { requestAuditLogQueryKey } from './detail/components/audit-log/useGetAuditLogsByRequestId'
import { RequestDetailQueryKey } from './useRequestDetail'

type SkipConditionsParams = {
  requestId: string
  blockId: string
}

const callSkipConditionsApi = async (token: string, { requestId, blockId }: SkipConditionsParams) => {
  const apiClient = new RequestsHttpClient({ authorization: token })
  const { data, error } = await apiClient.POST('/{id}/blocks/{blockId}/skip-conditions', {
    params: { path: { id: requestId, blockId } },
    body: {},
  })
  throwIfApiError(error)
  return data
}

export const useSkipConditions = () => {
  const { t } = useTranslation('requests')
  const { enqueueSnackbar } = useSnackbar()
  const { getToken } = useAuthenticatedUser()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: SkipConditionsParams) => {
      return callSkipConditionsApi(await getToken(), params)
    },
    onSuccess: (_data, variables) => {
      enqueueSnackbar(t('timeline-overview.step-skip-conditions-message'), { variant: 'success' })
      void queryClient.invalidateQueries({ queryKey: [RequestDetailQueryKey, variables.requestId] })
      void queryClient.invalidateQueries({ queryKey: [requestAuditLogQueryKey, variables.requestId] })
    },
    onError: () => {
      enqueueSnackbar(t('timeline-overview.step-skip-conditions-error'), { variant: 'error' })
    },
  })
}
